import { MarkTypes, richTextResolver } from '@storyblok/richtext';
import type { StoryblokRichTextNode } from '@storyblok/richtext';
import type { StoryblokRichTextDoc } from '~/.storyblok/types/storyblok';
import { EMPTY_RICHTEXT } from '~/utils/emptyRichtext';
import { h, createTextVNode, isVNode } from 'vue';
import type { VNodeArrayChildren, VNodeChild } from 'vue';

type Vars = Record<string, string | number | boolean | null | undefined>;

type TextResolver = (text: string) => VNodeChild | VNodeArrayChildren;

type ResolverOptions = {
    transformHref?: (href: string) => string;
    textResolver?: TextResolver;
};

const TOKEN_REGEX = /\{(\w+)\}/g;
type RichTextNode = StoryblokRichTextDoc['content'][number];

function interpolateRichText(doc: StoryblokRichTextDoc, vars: Vars): StoryblokRichTextDoc {
    if (!doc || !Array.isArray(doc.content)) return EMPTY_RICHTEXT;
    const walk = (node: RichTextNode): RichTextNode => {
        const nodeVar = { ...node };
        if (nodeVar.type === 'text') {
            nodeVar.text = nodeVar.text.replace(TOKEN_REGEX, (_: string, key: string) => {
                const v = vars?.[key];
                return v === null || v === undefined ? `{${key}}` : String(v);
            });
        }
        if ('content' in nodeVar && Array.isArray(nodeVar.content)) {
            nodeVar.content = nodeVar.content.map(walk);
        }
        return nodeVar;
    };

    return { ...doc, content: doc.content.map(walk) };
}

function asRendererDocument<T>(doc: StoryblokRichTextDoc): StoryblokRichTextNode<T> {
    // Richtext v3 uses enums and requires content on every node; the CLI models the same JSON more precisely.
    return doc as unknown as StoryblokRichTextNode<T>;
}

export function useRichTextRenderer() {
    return {
        render(doc: StoryblokRichTextDoc, vars: Vars = {}, overrides?: ResolverOptions) {
            const richtext = richTextResolver<VNodeChild>({
                renderFn: (tag, attrs, children) => h(tag, attrs, children ?? undefined),
                textFn: (text) => (overrides?.textResolver ? overrides.textResolver(text) : createTextVNode(text)),
                resolvers: {
                    [MarkTypes.LINK]: (node) => {
                        // The v3 resolver passes rendered text here, despite declaring node.text as string.
                        const text = node.text as VNodeChild;
                        const textChildren =
                            isVNode(text) && (typeof text.children === 'string' || Array.isArray(text.children))
                                ? text.children
                                : undefined;
                        if (node.attrs?.linktype === 'email') {
                            const rawAddress = node.attrs?.email || node.attrs?.href || node.attrs?.url || '';
                            if (!rawAddress) return textChildren ?? [];

                            const href = rawAddress.startsWith('mailto:') ? rawAddress : `mailto:${rawAddress}`;

                            const children = textChildren ?? text ?? [];
                            return h('a', { href }, children);
                        }

                        const raw = node.attrs?.href || node.attrs?.url || '';
                        const anchor = node.attrs?.anchor ? `#${node.attrs.anchor}` : '';
                        const transformed = overrides?.transformHref
                            ? overrides.transformHref(raw + anchor)
                            : raw + anchor;

                        const target = !node.attrs?.uuid ? '_blank' : node.attrs?.target;
                        const rel = target === '_blank' ? 'noopener noreferrer' : undefined;
                        const children = textChildren ?? text ?? [];

                        return h('a', { href: transformed, target, rel }, children);
                    }
                }
            });

            const interpolated = interpolateRichText(doc, vars);
            return richtext.render(asRendererDocument<VNodeChild>(interpolated));
        },

        renderToString(doc: StoryblokRichTextDoc, vars: Vars = {}) {
            const interpolated = interpolateRichText(doc, vars);
            const resolver = richTextResolver<string>();
            return resolver.render(asRendererDocument<string>(interpolated));
        }
    };
}
